import { Button,Input, ProgressBar, Toast } from 'components/atoms/index';
import * as styles from 'components/organisms/Table/Table.css'
import { Icon } from 'icons';
import { vars } from 'style/theme.css'
import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, MouseEvent } from 'react';
import { academyLearningScheduleApi, adminLearningScheduleApi, studentHomeworkApi, adminAcademyApi } from 'services/api';
import { Select, Pagination as AntPagination } from 'antd';
import Pagination from 'components/atoms/Pagination/Pagination';
import { useSessionSlug } from '@/hooks/useSessionSlug';
import { Modal } from '@/components/organisms/Modal/Modal';
import { getSlugPrefix } from '@/utils/slug';
const PAGE_SIZE = 10;
interface Homework {
    id: number
    name: string
    period: string
    progress: number
    status: '채점중' | '채점대기' | '채점완료' | '미응시'
    hasNew?: boolean
    needsGradingItems?: number
    gradedItems?: number // 채점 완료한 항목 수
    completedItems?: number // 학생이 완료한 항목 수
    studentName?: string
    studentUserId?: number
    academyStudentId?: number
    gradingStatus?: '채점중' | '채점대기' | '채점완료' | '미응시'
    reportStatus?: '발송대기' | '발송실패' | '발송완료'
    reportSentAt?: string | null
    isHomework?: boolean // 학습/숙제 구분
    academyName?: string
    completionDate?: string | null
    gradingRate?: number
    parentEmail?: string | null
    parentEmailMissing?: boolean
  }
  
  // 컬럼 렌더링용 메타 정보
const formatDateLabel = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}`;
  };

const formatDateTimeLabel = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };
  
const statusBadge = (status: Homework['status'], hasNew?: boolean) => {
    const baseClass = 'inline-flex items-center justify-center px-3 py-1 text-sm font-medium rounded-full';
    let badgeClass = 'text-blue-700 bg-blue-50';
    if (status === '채점중') {
      badgeClass = 'text-orange-700 bg-orange-50';
    } else if (status === '채점완료') {
      badgeClass = 'text-green-700 bg-green-50';
    }
    return (
      <div className='flex flex-col gap-1 items-center'>
        <span className={`${baseClass} ${badgeClass}`}>{status || '채점대기'}</span>
        {hasNew && (
          <span className='text-xs font-semibold text-red-500'>NEW</span>
        )}
      </div>
    );
  };
  
const columns = (
  type: 'academy' | 'student' | 'admin',
  handleResend?: (id: number) => void,
  resendLoadingId?: number | null,
  handleStudentClick?: (event: MouseEvent, row: Homework) => void
) => [
    { key: 'id'     as const, render: (row: Homework) => row.id },
    { 
      key: 'name'   as const, 
      render: (row: Homework) => (
        <div className="flex gap-2 items-center">
          <span>{row.name}</span>
          {row.hasNew && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
              NEW
            </span>
          )}
        </div>
      )
    },
    ...(type !== 'student' ? [{
      key: 'studentName' as const,
      render: (row: Homework) => row.studentName ? (
        <button
          type='button'
          className='text-indigo-600 hover:underline'
          onClick={(event) => handleStudentClick?.(event, row)}
        >
          {row.studentName}
        </button>
      ) : '-'
    }] : []),
    ...(type === 'admin' ? [{ key: 'academyName' as const, render: (row: Homework) => row.academyName || '-' }] : []),
    { key: 'period' as const, render: (row: Homework) => row.period },
    ...(type === 'student' ? [{ key: 'completionDate' as const, render: (row: Homework) => row.completionDate || '-' }] : []),
    { 
      key: 'progress' as const,
      render: (row: Homework) => {
        if (type === 'student') {
          return (
              <ProgressBar
              showTitle="숙제상태"
                containerClassName='lg:!flex-row lg:!gap-3'
                headerClassName='lg:order-2 !w-auto'
                titleClassName='lg:!hidden '
                progress={row.progress || 0}
              />
          );
        }

        const showNewBadge = row.hasNew && (
          <span className='text-xs font-semibold text-red-500'>NEW · 채점 필요</span>
        );

        return (
          <div className='flex flex-col gap-2 w-full'>
            <ProgressBar
              showTitle="숙제 진행률"
              containerClassName='lg:!flex-row lg:!gap-3'
              headerClassName='lg:order-2 !w-auto'
              titleClassName='lg:!hidden '
              progress={row.progress || 0}
            />
            <div className='flex justify-between items-center text-xs text-gray-500'>
              <span>채점률 {row.gradingRate ?? 0}%</span>
              {showNewBadge}
            </div>
          </div>
        );
      }
    },
    { key: 'status' as const, render: (row: Homework) => statusBadge(row.status, row.hasNew) },
    ...(type !== 'student' ? [{ key: 'reportStatus' as const, render: (row: Homework) => {
      const sentAtLabel = formatDateTimeLabel(row.reportSentAt);
      // 발송 실패인 경우에만 재발송 버튼 노출 (발송 대기 상태에서는 숨김)
      const showResend = row.reportStatus === '발송실패';
      const parentEmailMissing = Boolean(row.parentEmailMissing || (!row.parentEmail && row.reportStatus === '발송실패'));
      return (
        <div className="flex flex-col gap-2">
      <div className="flex gap-2 items-center">
        <p className={ reportStatusColor(row.reportStatus || '발송대기') }>{row.reportStatus || '발송대기'}</p>
            {showResend && handleResend && (
          <Button 
            size="small" 
            outline 
            disabled={resendLoadingId === row.id}
            onClick={(e) => {
              e.stopPropagation();
              handleResend(row.id);
            }}
          >
            {resendLoadingId === row.id ? '발송중...' : '재발송'}
          </Button>
        )}
      </div>
          <span className="text-xs text-gray-500">
            {sentAtLabel ? `발송일시 ${sentAtLabel}` : '발송 이력 없음'}
          </span>
          <span className={`text-xs ${parentEmailMissing ? 'text-red-500' : 'text-gray-500'}`}>
            {parentEmailMissing
              ? '학부모 이메일이 등록되어 있지 않아 발송에 실패했습니다.'
              : ''}
          </span>
        </div>
      );
    } }] : []),
  ]
  
  function reportStatusColor(status: Homework['reportStatus']) {
    switch (status) {
      case '발송대기': return 'text-blue-500'
      case '발송실패': return 'text-red-500'
      case '발송완료': return 'text-green-500'
      default: return 'text-blue-500'
    }
  }
  
const computeHomeworkMeta = (row: any) => {
  const totalItems = Number(row.totalItems ?? row.completedItems ?? 0);
  const completedItems = Number(row.completedItems ?? totalItems ?? 0);
  const gradedItems = Number(row.gradedItems ?? 0);
  const needsGradingItems = Number(
    row.needsGradingItems ?? Math.max(totalItems - gradedItems, 0)
  );
  let status: Homework['status'] = row.status || '채점대기';
  let hasNew = row.hasNew ?? false;

  const denominator = totalItems > 0 ? totalItems : completedItems;
  const gradingRate = denominator > 0
    ? Math.round((Math.min(gradedItems, denominator) / denominator) * 100)
    : (status === '채점완료' ? 100 : 0);

  if (row.isHomework ?? true) {
    if (denominator > 0 && needsGradingItems === 0 && gradedItems >= denominator) {
      status = '채점완료';
      hasNew = false;
    } else if (gradedItems > 0 || completedItems > 0) {
      status = gradingRate > 0 ? '채점중' : '채점대기';
      hasNew = true;
    } else {
      status = '채점대기';
      hasNew = false;
    }
  }

  return { status, hasNew, gradingRate };
};
  


interface HomeworkTableProps {
  type?: 'academy' | 'student' | 'admin'
}

interface AcademyOption {
  value: string;
  label: string;
  academyId?: number;
  academyCode?: string;
}

export default function HomeworkTable({ type = 'student' }: HomeworkTableProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { role: sessionRole } = useSessionSlug();
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [homeworkList, setHomeworkList] = useState<Homework[]>([]);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState<number | null>(null); // 재발송 중인 스케줄 ID
  const [toast, setToast] = useState<{ message: string; status: 'success' | 'error' | 'info' } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAcademy, setSelectedAcademy] = useState<string>('all');
  const [academyOptions, setAcademyOptions] = useState<AcademyOption[]>([{ value: 'all', label: '전체 학원' }]);
  const [academyLoading, setAcademyLoading] = useState(false);

  const statusSummary = useMemo(() => {
    const counts: Record<'채점대기' | '채점중' | '채점완료', number> = {
      채점대기: 0,
      채점중: 0,
      채점완료: 0
    };
    let totalProgress = 0;
    homeworkList.forEach((row) => {
      const normalizedStatus = row.status === '미응시' ? '채점대기' : row.status;
      const key = (normalizedStatus || '채점대기') as keyof typeof counts;
      counts[key] = (counts[key] || 0) + 1;
      totalProgress += Number(row.progress || 0);
    });
    const total = homeworkList.length;
    const averageProgress = total ? Math.round(totalProgress / total) : 0;
    return { counts, total, averageProgress };
  }, [homeworkList]);

  useEffect(() => {
    if (type !== 'admin') return;
    const loadAcademies = async () => {
      setAcademyLoading(true);
      try {
        const res = await adminAcademyApi.list({ q: '', contractStatus: undefined, page: 0, size: 100 });
        const data = res?.data || res;
        const pageData = data?.data || data;
        const content = pageData?.content || [];
        const options = content.map((item: any) => ({
          value: String(item.id ?? item.code ?? item.academyCode ?? item.name ?? ''),
          label: item.name || item.academyName || item.code || `학원 ${item.id}`,
          academyId: item.id ?? item.academyId ?? undefined,
          academyCode: item.code || item.academyCode || undefined,
        }));
        setAcademyOptions([{ value: 'all', label: '전체 학원' }, ...options]);
      } catch (error) {
        console.error('학원 목록 조회 실패:', error);
        setAcademyOptions([{ value: 'all', label: '전체 학원' }]);
      } finally {
        setAcademyLoading(false);
      }
    };
    loadAcademies();
  }, [type]);

  useEffect(() => {
    if (type !== 'admin') return;
    const exists = academyOptions.some((opt) => opt.value === selectedAcademy);
    if (!exists) {
      setSelectedAcademy('all');
    }
  }, [academyOptions, selectedAcademy, type]);
  
  const storedRole = typeof window !== 'undefined' ? (localStorage.getItem('academy_role') || '') : '';
  const normalizedRole = (sessionRole || storedRole || '').toLowerCase();
  const isTeacherRole = normalizedRole.includes('teacher');
  
  const slugPrefix = getSlugPrefix(location.pathname);
  const buildStudentHomeworkDetailPath = (scheduleId: number) =>
    slugPrefix ? `${slugPrefix}/mypage/homework/detail?id=${scheduleId}` : `/mypage/homework/detail?id=${scheduleId}`;
  const buildHomeworkListPath = () => (slugPrefix ? `${slugPrefix}/homework/list` : '/homework/list');

  const handleRowClick = async (homework: Homework) => {
    if (type === 'academy') {
      // TODO: 원장/강사 구분 로직 필요
      // 현재는 채점 상세 페이지로 이동
      navigate(`/academy/learning/grading/detail?id=${homework.id}`);
      
      // 원장인 경우: 학생 기본정보 화면으로 이동
      // navigate(`/academy/student/detail/${homework.studentUserId}`);
    } else if (type === 'admin') {
      navigate(`/admin/learning/grading/detail/${homework.id}`);
    } else {
      const completionRate = Math.min(100, Math.max(0, Number(homework.progress ?? 0)));
      const homeworkListPath = buildHomeworkListPath();
      const detailPath = buildStudentHomeworkDetailPath(homework.id);

      if (completionRate <= 0) {
        await Modal.alert({ message: '먼저 숙제를 완료해주세요!' });
        navigate(homeworkListPath);
        return;
      }

      if (completionRate < 100) {
        const goReport = await Modal.confirm({
          message: '먼저 숙제를 완료해주세요!',
          okText: '성적표 보기',
          cancelText: '확인',
        });

        if (goReport) {
          navigate(detailPath);
        } else {
          navigate(homeworkListPath);
        }
        return;
      }

      navigate(detailPath);
    }
  };

  const handleToggleAvailable = () => {
    setShowOnlyAvailable(!showOnlyAvailable);
  };

  const handleStudentNameClick = (event: MouseEvent, row: Homework) => {
    event.stopPropagation();
    if (type === 'admin') {
      const targetId = row.academyStudentId || row.studentUserId;
      if (!targetId) return;
      navigate(`/admin/student/detail/${targetId}`);
    } else if (type === 'academy') {
      if (isTeacherRole) {
        setToast({ message: '강사 계정은 학생 기본정보를 열람할 수 없습니다.', status: 'info' });
        return;
      }
      const targetId = row.academyStudentId || row.studentUserId;
      if (!targetId) return;
      navigate(`/academy/student/detail/${targetId}`);
    }
  };

  const handleResend = async (scheduleId: number) => {
    if (!window.confirm('성적표를 재발송하시겠습니까?')) {
      return;
    }

    setResendLoading(scheduleId); // 로딩 시작

    try {
      const resendApi = type === 'admin' ? adminLearningScheduleApi : academyLearningScheduleApi;
      const response = await resendApi.resendReport(scheduleId);
      
      if (response.success) {
        setToast({ message: '이메일 발송 완료', status: 'success' });
        // 리스트 새로고침
        fetchHomeworkList();
      } else {
        // 이메일이 없는 경우
        if (response.data?.code === 'NO_EMAIL') {
          setToast({ message: response.data.message || '학생의 학부모 이메일을 입력해주세요', status: 'error' });
          
          // TODO: 원장/강사 역할 구분 로직 추가 필요
          // 현재는 역할 정보가 없어서 학생 정보 페이지로 이동하지 않음
          // const userRole = localStorage.getItem('academy_user_role');
          // if (userRole === 'DIRECTOR' || userRole === 'OWNER') {
          //   // 원장: 학생 기본정보 화면으로 이동
          //   const studentUserId = response.data.studentUserId;
          //   navigate(`/academy/student/detail/${studentUserId}`);
          // } else {
          //   // 강사: 팝업만 표시 (페이지 이동 없음)
          // }
        } else {
          setToast({ message: response.message || '이메일 발송 실패', status: 'error' });
        }
      }
    } catch (error: any) {
      console.error('성적표 재발송 실패:', error);
      setToast({ 
        message: error.response?.data?.message || '이메일 발송 중 오류가 발생했습니다.', 
        status: 'error' 
      });
    } finally {
      setResendLoading(null); // 로딩 종료
    }
  };

  const fetchHomeworkList = async () => {
    setLoading(true);
    try {
      let response;
      
      if (type === 'student') {
        // 학생용 API 호출
        response = await studentHomeworkApi.list({
          search: searchQuery || undefined,
          onlyAvailable: showOnlyAvailable
        });
      } else if (type === 'academy') {
        // 학원용 API 호출
        response = await academyLearningScheduleApi.gradingList({
        search: searchQuery || undefined,
        onlyNeedGrading: showOnlyAvailable
      });
      } else {
        const selected = selectedAcademy === 'all'
          ? {}
          : (() => {
              const found = academyOptions.find((opt: AcademyOption) => opt.value === selectedAcademy);
              if (!found) return {};
              if (found.academyId != null) return { academyId: found.academyId };
              if (found.academyCode) return { academyCode: found.academyCode };
              return {};
            })();
        response = await adminLearningScheduleApi.gradingList({
          search: searchQuery || undefined,
          onlyNeedGrading: showOnlyAvailable,
          ...selected,
        });
      }
      
      if (response.success && response.data) {
        console.log('숙제 목록 데이터:', response.data.list);
        
        if (type === 'student') {
          // 학생용: 백엔드 데이터를 테이블 형식에 맞게 변환
          const processedList = (response.data.list || []).map((item: any) => {
            // 상태 텍스트 변환
            let statusText: Homework['status'] = '채점대기';
            if (item.gradingStatus === 'COMPLETED') {
              statusText = '채점완료';
            } else if (item.gradingStatus === 'IN_PROGRESS') {
              statusText = '채점중';
            } else if (item.gradingStatus === 'NOT_STARTED') {
              statusText = '미응시';
            } else {
              statusText = '채점대기';
            }
            
            return {
              id: item.id,
              name: item.title,
              period: `${item.startDate} ~ ${item.endDate}`,
              progress: item.completionRate || 0,
              status: statusText,
              completionDate: formatDateLabel(item.completionDate) || null,
              hasNew: false,
              studentName: undefined,
              studentUserId: undefined,
              gradingStatus: statusText,
              reportStatus: undefined,
              isHomework: item.scoringRequired
            };
          });
          
          setHomeworkList(processedList);
        } else if (type === 'academy') {
          const processedList = (response.data.list || []).map((item: any) => {
            const meta = computeHomeworkMeta(item);
            return {
              ...item,
              ...meta,
              reportSentAt: item.reportSentAt,
            };
          });
          setHomeworkList(processedList);
        } else {
          const processedList = (response.data.list || []).map((item: any) => {
            const meta = computeHomeworkMeta(item);
            return {
              ...item,
              ...meta,
              id: item.id,
              name: item.name,
              period: item.period,
              progress: item.progress || 0,
              studentName: item.studentName,
              academyName: item.academyName,
              reportStatus: item.reportStatus,
              reportSentAt: item.reportSentAt,
            };
          });
          setHomeworkList(processedList);
          setCurrentPage(1);
        }
      }
    } catch (error) {
      console.error('숙제 목록 조회 실패:', error);
      setHomeworkList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHomeworkList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnlyAvailable, type, selectedAcademy]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(homeworkList.length / PAGE_SIZE));
    setCurrentPage((prev) => Math.min(prev, maxPage));
  }, [homeworkList]);

  // Toast 자동 닫기
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      fetchHomeworkList();
    }
  };

  return (
        <>
        <main>
            <section className={`pt-3 lg:px-5 ${type === 'academy' ? 'flex justify-between items-center' : ''}`}>
              <div className='mb-2'>
                <Input
                  placeholder='검색어를 입력해주세요.'
                  className='max-w-[500px]'
                  icon={<Icon name="search" size={20} color={vars.color.gray[300]} />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                />
              </div>
                {type === 'admin' && (
                  <div className='flex gap-4 items-center'>
                    <div className='flex items-center gap-2 min-w-[240px]'>
                      <span className='text-sm text-gray-500 whitespace-nowrap'>학원 선택</span>
                      <Select
                        style={{ minWidth: 180 }}
                        value={selectedAcademy}
                        onChange={(value) => setSelectedAcademy(value)}
                        loading={academyLoading}
                        options={academyOptions.map((opt: AcademyOption) => ({ label: opt.label, value: opt.value }))}
                      />
                    </div>
                    <button 
                      className={`flex gap-1 justify-center items-center text-sm ${showOnlyAvailable ? 'text-indigo-500' : 'text-gray-500'}`}
                      onClick={handleToggleAvailable}
                    >
                        <Icon name="check" size={20} className={showOnlyAvailable ? '*:fill-indigo-500' : '*:fill-gray-400'} />
                        <span>채점 필요한 숙제만 보기</span>
                    </button>
                  </div>
                )}
                {type !== 'student' && type !== 'admin' && (
                  <button 
                    className={`flex gap-1 justify-center items-center text-sm ${showOnlyAvailable ? 'text-indigo-500' : 'text-gray-500'}`}
                    onClick={handleToggleAvailable}
                  >
                      <Icon name="check" size={20} className={showOnlyAvailable ? '*:fill-indigo-500' : '*:fill-gray-400'} />
                      <span>채점 필요한 숙제만 보기</span>
                  </button>
                )}
            </section>

            {/* {type === 'student' && (
              <section className='grid gap-3 px-5 pt-2 pb-4 lg:px-0 lg:grid-cols-4'>
                <div className='flex flex-col justify-between p-4 text-sm bg-gray-50 rounded-2xl border border-gray-100'>
                  <p className='text-gray-500'>총 숙제</p>
                  <p className='text-2xl font-bold text-gray-900'>{statusSummary.total}</p>
                  <span className='text-xs text-gray-400'>평균 진행률 {statusSummary.averageProgress}%</span>
                </div>
                {(['채점대기', '채점중', '채점완료'] as const).map((label) => (
                  <div key={label} className='flex flex-col justify-between p-4 text-sm bg-white rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]'>
                    <p className='text-gray-500'>{label}</p>
                    <p className='text-2xl font-bold text-gray-900'>{statusSummary.counts[label]}</p>
                    <span className='text-xs text-gray-400'>전체 대비 {statusSummary.total ? Math.round((statusSummary.counts[label] / statusSummary.total) * 100) : 0}%</span>
                  </div>
                ))}
              </section>
            )} */}

            <section className='pt-5'>
                {loading ? (
                  <p className='py-10 text-center'>로딩 중...</p>
                ) : homeworkList.length === 0 ? (
                  <p className='py-10 text-center'>리스트가 없습니다.</p>
                ) : (
                  <div className=''>
                      {/* 테이블 해더 pc 용*/}
                      <ul className={styles.headerList}>
                          <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
                          <li className={`${styles.headerItem}`}>숙제명</li>
                          {type !== 'student' && (
                              <li className={`${styles.headerItem} max-w-[120px] justify-center`}>학생명</li>
                          )}
                          {type === 'admin' && (
                              <li className={`${styles.headerItem} max-w-[160px] justify-center`}>학원명</li>
                          )}
                          <li className={`${styles.headerItem} max-w-[240px]`}>숙제기간</li>
                          {type === 'student' && (
                              <li className={`${styles.headerItem} max-w-[160px]`}>완료일자</li>
                          )}
                          <li className={`${styles.headerItem} max-w-[200px]`}>숙제상태</li>
                          <li className={`${styles.headerItem} max-w-[200px] lg:flex justify-center`}>채점상태</li>
                          {type !== 'student' && (
                              <li className={`${styles.headerItem} max-w-[200px] `}>성적표 발송 내역</li>
                          )}
                      </ul>
                      <div className='flex flex-col'>
                          {homeworkList
                            .slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
                            .map((row, rowIndex) => {
                              const globalIndex = (currentPage - 1) * PAGE_SIZE + rowIndex;
                              const descendingNo = homeworkList.length > 0
                                ? homeworkList.length - globalIndex
                                : 0;
                              return (
                              <ul 
                                  key={row.id} 
                                  className={`${styles.bodyList} lg:shadow-none table-card cursor-pointer hover:bg-gray-50 transition-colors`}
                                  onClick={() => handleRowClick(row)}
                              >
                          {columns(type, handleResend, resendLoading, handleStudentNameClick).map(({ key, render }) => (
                                  <li
                                  key={key}
                                  className={`${styles.bodyItem} ${
                                      key === 'id' ? 'max-w-[80px] hidden lg:flex justify-center' :
                                      key === 'studentName' ? 'max-w-[120px] lg:flex justify-center' :
                                      key === 'academyName' ? 'max-w-[160px] lg:flex justify-center' :
                                      key === 'period' ? 'text-sm text-gray-500 max-w-[240px] lg:static' :
                                      key === 'completionDate' ? 'text-sm text-gray-500 max-w-[160px] lg:flex justify-center' :
                                      key === 'progress' ? 'lg:max-w-[200px] w-full' :
                                      key === 'status' ? 'absolute top-5 right-5 lg:static max-w-[200px] lg:flex justify-center' :
                                      key === 'reportStatus' ? 'max-w-[200px] lg:flex ' :
                                      ''
                                  }`}
                                  >
                                  {key === 'id' ? descendingNo : render(row)}
                                  </li>
                              ))}
                              </ul>
                          )})}
                      </div>
                      {homeworkList.length > 0 && (
                        <div className='flex flex-col gap-2 items-end mt-6'>
                          {type === 'admin' ? (
                            <AntPagination
                              current={currentPage}
                              total={homeworkList.length}
                              pageSize={PAGE_SIZE}
                              showSizeChanger={false}
                              showQuickJumper={false}
                              showTotal={(total, range) => {
                                if (!total) return '0-0 / 총 0개';
                                const start = Math.max(total - range[1] + 1, 1);
                                const end = Math.max(total - range[0] + 1, 1);
                                return `${start}-${end} / 총 ${total}개`;
                              }}
                              onChange={(page) => setCurrentPage(page)}
                            />
                          ) : (
                            <Pagination
                              total={homeworkList.length}
                              current={currentPage}
                              pageSize={PAGE_SIZE}
                              onChange={(page) => setCurrentPage(page)}
                            />
                          )}
                        </div>
                      )}
                  </div>
                )}
            </section>
        </main>
        
        {/* Toast 메시지 */}
        {toast && (
          <Toast
            message={toast.message}
            status={toast.status}
            isVisible={!!toast}
          />
        )}
        </>
  )
}